import { CallToolResult } from '@modelcontextprotocol/sdk/types.js';
import { Ok } from 'ts-results-es';
import { z } from 'zod';

import { CustomViewNotAllowedError } from '../../../errors/mcpToolError.js';
import { useRestApi } from '../../../restApiInstance.js';
import { WebMcpServer } from '../../../server.web.js';
import { resourceAccessChecker } from '../resourceAccessChecker.js';
import { WebTool } from '../tool.js';
import {
  buildDataToolResult,
  DataToolResult,
  dataToolResultToCallToolResult,
} from './dataToolResult.js';

const paramsSchema = {
  customViewId: z.string(),
  viewFilters: z
    .record(z.string())
    .optional()
    .describe('Optional map of view filter field names to values.'),
};

export type GetCustomViewDataError = {
  type: 'custom-view-not-allowed';
  message: string;
};

export const getGetCustomViewDataTool = (server: WebMcpServer): WebTool<typeof paramsSchema> => {
  const getCustomViewDataTool = new WebTool({
    server,
    name: 'get-custom-view-data',
    description: [
      "Retrieves comma-separated value (CSV) data for a Tableau Custom View (saved/personalized view state), including the user's filters.",
      'Requires the custom view LUID from the content URL (not the published view id).',
      'For published views, use the tool to get view data by view id instead.',
    ].join(' '),
    paramsSchema,
    annotations: {
      title: 'Get Custom View Data',
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
    callback: async ({ customViewId, viewFilters }, extra): Promise<CallToolResult> => {
      return await getCustomViewDataTool.logAndExecute<DataToolResult>({
        extra,
        args: { customViewId, viewFilters },
        callback: async () => {
          const isAllowedResult = await resourceAccessChecker.isCustomViewAllowed({
            customViewId,
            extra,
          });

          if (!isAllowedResult.allowed) {
            return new CustomViewNotAllowedError(isAllowedResult.message).toErr();
          }

          const csv = await useRestApi({
            ...extra,
            jwtScopes: getCustomViewDataTool.requiredApiScopes,
            callback: async (restApi) => {
              return await restApi.viewsMethods.getCustomViewData({
                customViewId,
                siteId: restApi.siteId,
                viewFilters,
              });
            },
          });

          // Offload to S3 (returning a presigned URL) when configured, otherwise
          // carry the raw CSV for an inline text result. Falls back to inline on
          // any S3 failure.
          return new Ok(
            await buildDataToolResult({
              csv,
              resourceId: customViewId,
              config: extra.config,
              toolName: getCustomViewDataTool.name,
              keyPrefixSegment: 'custom-view-data/',
            }),
          );
        },
        constrainSuccessResult: (dataToolResult) => {
          return {
            type: 'success',
            result: dataToolResult,
          };
        },
        getSuccessResult: (dataToolResult) => dataToolResultToCallToolResult(dataToolResult),
      });
    },
  });

  return getCustomViewDataTool;
};
